import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Logo from './Logo';
import Icon from './Icon';
import { saldoDeCreditos } from './logada/creditos';
import { urlDoAvatar } from '../utils/avatar';
import { Link, caminhoNo, useIdioma } from '../i18n';
import { lerPrefixo } from '../config/seo';
import { useFormato } from '../i18n/formato';
import { apiFetch, logoutRequest } from '../config/api';
import '../styles/NewAppShell.css';

/**
 * Moldura da área logada nova, ao lado do AppShell, que segue servindo as
 * telas antigas. Cada tela nova a traz junto, então voltar uma tela é trocar a
 * linha dela no App.js.
 *
 * Sem barra no topo: o caminho e as ações sobem para o cabeçalho da página, e
 * o saldo de créditos desce para o pé do menu.
 *
 *   <NewAppShell
 *     user={user}
 *     crumbs={[{ rotulo: 'Projetos', to: '/projects' }, { rotulo: 'Diafer' }]}
 *     titulo="Diafer"
 *     subtitulo="Nota 91, igual à execução anterior."
 *     actions={<Link …>Nova execução</Link>}
 *   >
 *     …conteúdo…
 *   </NewAppShell>
 */

/* "Criar projeto" saiu do menu: é ação, e fica no cabeçalho do Painel e de
   Projetos. Privacidade e Termos descem para o pé. */
const NAV = [
  {
    itens: [
      { to: '/user-home', chave: 'menu.painel', icon: 'grid' },
      { to: '/projects', chave: 'menu.projetos', icon: 'folder' }
    ]
  },
  {
    chave: 'menu.conta',
    itens: [
      { to: '/settings', chave: 'menu.configuracoes', icon: 'user' },
      { to: '/plans', chave: 'menu.planoEUso', icon: 'gauge' },
      { to: '/payments', chave: 'menu.pagamentos', icon: 'card' }
    ]
  },
  { chave: 'menu.ajuda', itens: [{ to: '/docs', chave: 'menu.documentacao', icon: 'book' }] }
];

/** Barra inferior no celular: os mesmos quatro destinos da moldura antiga. */
const ABAS = [
  { to: '/user-home', chave: 'menu.painel', icon: 'grid' },
  { to: '/projects', chave: 'menu.projetos', icon: 'folder' },
  { to: '/plans', chave: 'menu.planoEUso', icon: 'gauge' },
  { to: '/settings', chave: 'menu.configuracoes', icon: 'gear' }
];

const capitular = (texto) => (texto ? texto.charAt(0).toUpperCase() + texto.slice(1) : '');

const NewAppShell = ({ user, crumbs = [], titulo, subtitulo, actions, children }) => {
  const { t, idioma } = useIdioma();
  const { numero } = useFormato();
  const { pathname } = useLocation();
  const [creditos, setCreditos] = useState(null);

  // shortcut: a mesma busca do AICreditsDisplay, até a moldura antiga ser apagada.
  useEffect(() => {
    let ativo = true;

    apiFetch('/ai/credits')
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (!ativo || !json) return;
        setCreditos(
          saldoDeCreditos({
            total: json.total_credits,
            usados: json.credits_used,
            extra: json.credits_extra,
            restantes: json.credits_remaining
          })
        );
      })
      .catch(() => {});

    return () => {
      ativo = false;
    };
  }, []);

  /* Sem o prefixo do idioma: o menu leva /plans para /pt-br/plans. */
  const aqui = lerPrefixo(pathname).caminho;
  const ativo = (to) => aqui === to || (to !== '/user-home' && aqui.startsWith(to));

  /* Sair de verdade exige o servidor (o cookie é httpOnly), e a recarga
     completa garante que nenhum estado em memória sobreviva. Igual ao AppShell. */
  const sair = async () => {
    await logoutRequest();
    window.location.href = caminhoNo(idioma, '/login');
  };

  /* No menu lateral e, até 1000px, onde o menu some, na barra do topo. */
  const botaoSair = (
    <button type="button" onClick={sair} className="lg-sair" aria-label={t('menu.sair')} title={t('menu.sair')}>
      <Icon name="signOut" size={18} />
    </button>
  );

  const ultimo = crumbs.length - 1;

  return (
    <div className="lg">
      <aside className="lg-lado">
        <Link to="/user-home" className="lg-marca">
          <Logo size={26} />
        </Link>

        <nav className="lg-nav" aria-label={t('area.navegacao')}>
          {NAV.map((grupo, i) => (
            <div className="lg-grupo" key={grupo.chave || i}>
              {grupo.chave && <span className="lg-grupo-nome">{t(grupo.chave)}</span>}
              {/* Cobranças são do gestor (Fase 4a). */}
              {grupo.itens.filter((item) => !(item.to === '/payments' && user?.papel === 'analista')).map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="lg-item"
                  aria-current={ativo(item.to) ? 'page' : undefined}
                >
                  <Icon name={item.icon} size={18} />
                  {t(item.chave)}
                </Link>
              ))}
            </div>
          ))}
        </nav>

        <div className="lg-pe">
          {creditos && (
            <div className="lg-creditos">
              <span className="lg-creditos-rotulo">{t('painel.creditos')}</span>
              <span className="lg-creditos-valor">
                {numero(creditos.restantes)}{' '}
                <span>{t('area.de', { total: numero(creditos.capacidade) })}</span>
              </span>
              <span
                className={`lg-barra${creditos.pct <= 20 ? ' is-baixo' : ''}`}
                role="img"
                aria-label={t('area.creditosRestantes', {
                  restantes: numero(creditos.restantes),
                  total: numero(creditos.capacidade)
                })}
              >
                <span style={{ width: `${creditos.pct}%` }} />
              </span>
              <span className="lg-creditos-detalhe">
                {creditos.extra > 0
                  ? t('area.creditosOrigem', {
                      mes: numero(creditos.doMes),
                      extra: numero(creditos.extra)
                    })
                  : t('area.creditosDoMes', { mes: numero(creditos.doMes) })}
              </span>
            </div>
          )}

          <div className="lg-usuario">
            <img className="lg-avatar" src={urlDoAvatar(user?.avatar)} alt="" />
            <span className="lg-usuario-texto">
              <b>{user?.name}</b>
              {user?.plan && <span>{t('conta.plano', { plano: capitular(user.plan) })}</span>}
            </span>
            {botaoSair}
          </div>

          <div className="lg-legal">
            <Link to="/privacy-policy">{t('area.privacidade')}</Link>
            <span aria-hidden="true"> · </span>
            <Link to="/terms-of-service">{t('area.termos')}</Link>
          </div>
        </div>
      </aside>

      <div className="lg-topo">
        <Link to="/user-home" className="lg-marca">
          <Logo size={24} />
        </Link>
        {creditos && (
          <span className="lg-topo-creditos">
            <b>{numero(creditos.restantes)}</b> {t('area.creditosCurto')}
          </span>
        )}
        {botaoSair}
      </div>

      <main className="lg-main">
        <div className="lg-conteudo">
          {crumbs.length > 0 && (
            <nav className="lg-caminho" aria-label={t('area.caminho')}>
              {crumbs.map((c, i) => (
                <React.Fragment key={`${i}-${c.rotulo}`}>
                  {i > 0 && <span aria-hidden="true">/</span>}
                  {i < ultimo && c.to ? (
                    <Link to={c.to}>{c.rotulo}</Link>
                  ) : (
                    <span aria-current={i === ultimo ? 'page' : undefined}>{c.rotulo}</span>
                  )}
                </React.Fragment>
              ))}
            </nav>
          )}

          {titulo && (
            <header className="lg-cabeca">
              <div className="lg-cabeca-texto">
                <h1>{titulo}</h1>
                {subtitulo && <p>{subtitulo}</p>}
              </div>
              {actions && <div className="lg-acoes">{actions}</div>}
            </header>
          )}

          {children}
        </div>
      </main>

      <nav className="lg-abas" aria-label={t('area.navegacao')}>
        {ABAS.map((aba) => (
          <Link
            key={aba.to}
            to={aba.to}
            className="lg-aba"
            aria-current={ativo(aba.to) ? 'page' : undefined}
          >
            <Icon name={aba.icon} size={20} />
            <span>{t(aba.chave)}</span>
          </Link>
        ))}
      </nav>
    </div>
  );
};

export default NewAppShell;
